import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Menu, MenuItem, Button, Checkbox, TextField, Chip, Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import ConfirmModal from '../../components/common/ConfirmModal';
import AdminManageModal from '../../components/Admin/AdminManageModal';
import { useNavigate } from 'react-router-dom';
import AdminTabs from '../../components/Admin/AdminTabs';
import { adminBlacklistRegisterApi, adminBlacklistReleaseApi, adminResetUserPasswordApi, getAdminInboxThreadByUserIdApi, getAdminUsersApi } from '../../api';

const PAGE_SIZE = 20;

const UserManagement = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedTerm, setSearchedTerm] = useState('');
  const [searchSeq, setSearchSeq] = useState(0);
  const searchDebounceTimerRef = useRef(null);
  // status filter: all | ACTIVE | BLACKLIST
  const [roleFilter, setRoleFilter] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState('');
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sort, setSort] = useState('all');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [confirmChatOpen, setConfirmChatOpen] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [confirmBlacklistRegisterOpen, setConfirmBlacklistRegisterOpen] = useState(false);
  const [confirmBlacklistReleaseOpen, setConfirmBlacklistReleaseOpen] = useState(false);
  const [passwordOpenUserId, setPasswordOpenUserId] = useState(null);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordResetDoneOpen, setPasswordResetDoneOpen] = useState(false);
  const [adminManageOpen, setAdminManageOpen] = useState(false);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleOpenChat = async (userId) => {
    try {
      const res = await getAdminInboxThreadByUserIdApi(userId);
      const threadId = res?.data?.thread?.thread_id;
      if (!threadId) {
        alert('대화방 정보를 불러오지 못했습니다.');
        return;
      }
      navigate(`/inbox/${threadId}`);
    } catch (e) {
      if (e?.status === 401 || e?.suppressAlert) return;
      alert(e?.message || '대화방 이동에 실패했습니다.');
    }
  };

  const openChatConfirm = (user) => {
    setSelectedChatUser({ userId: user.userId, name: user.name });
    setConfirmChatOpen(true);
  };

  const closeChatConfirm = () => {
    setConfirmChatOpen(false);
    setSelectedChatUser(null);
  };

  const confirmOpenChat = async () => {
    const userId = selectedChatUser?.userId;
    if (!userId) return;
    setConfirmChatOpen(false);
    await handleOpenChat(userId);
    setSelectedChatUser(null);
  };

  const filterOptions = [
    { key: 'all', label: '전체' },
    { key: 'ACTIVE', label: '일반사용자' },
    { key: 'BLACKLIST', label: '블랙리스트' },
  ];

  const currentFilterLabel = filterOptions.find((opt) => opt.key === roleFilter)?.label || '전체';

  const sortOptions = [
    { key: 'all', label: '정렬' },
    { key: 'online', label: '현재접속자' },
    { key: 'createdAt', label: '가입일' },
    { key: 'postCount', label: '작성글수' },
    { key: 'commentCount', label: '댓글수' },
  ];
  const currentSortLabel = sortOptions.find((opt) => opt.key === sort)?.label || '정렬';
  const isSortOpen = Boolean(sortAnchorEl);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (key) => {
    setRoleFilter(key);
    setFilterAnchorEl(null);
    setPage(1);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (key) => {
    setSort(key);
    setSortAnchorEl(null);
    setPage(1);
  };

  const handleSearch = () => {
    const next = searchTerm.trim();
    setSearchedTerm(next);
    setSearchSeq((v) => v + 1);
    setPage(1);
  };

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setSearchedTerm(searchTerm.trim());
      setSearchSeq((v) => v + 1);
      setPage(1);
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return '일반사용자';
      case 'BLACKLIST':
        return '블랙리스트';
      default:
        return status || '-';
    }
  };

  const renderOnlineBadge = (user) => {
    if (!user) return null;
    if (user.isOnline) {
      return (
        <Chip
          label="접속중"
          size="small"
          sx={{ bgcolor: '#e8f5e9', color: '#1b5e20', height: 22, fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        label="오프라인"
        size="small"
        sx={{ bgcolor: '#f5f5f5', color: '#6b7280', height: 22, fontWeight: 700 }}
      />
    );
  };

  const totalPages = Math.max(1, Math.ceil(totalUsers / PAGE_SIZE));

  useEffect(() => {
    let cancelled = false;
    async function fetchUsers() {
      try {
        setUsersError('');
        const res = await getAdminUsersApi({
          keyword: searchedTerm || undefined,
          status: roleFilter !== 'all' ? roleFilter : undefined,
          page,
          limit: PAGE_SIZE,
          sort: sort === 'all' ? 'latest' : sort,
        });

        const result = res?.data || res || {};
        const items = result?.items || [];
        const total = Number(result?.total || 0);
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

        if (!cancelled) {
          setUsers(items);
          setTotalUsers(total);
          if (page > totalPages) {
            setPage(totalPages);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setUsers([]);
          setTotalUsers(0);
          setUsersError(e?.message || '사용자 목록을 불러오지 못했습니다.');
        }
      } finally {
        // no-op
      }
    }

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [searchedTerm, roleFilter, searchSeq, page, sort]);

  const toggleSelectedUser = (userId) => {
    setSelectedUserIds((prev) => {
      const idStr = String(userId);
      if (prev.includes(idStr)) return prev.filter((x) => x !== idStr);
      return [...prev, idStr];
    });
  };

  const togglePasswordPanel = (userId) => {
    const idStr = String(userId);
    setPasswordOpenUserId((prev) => (String(prev) === idStr ? null : idStr));
    setPasswordDraft('');
  };

  const canResetPassword = (user) => {
    return user?.role === 'MEMBER' && user?.status === 'ACTIVE';
  };

  const handleResetPassword = async (user) => {
    if (!canResetPassword(user)) {
      alert('비밀번호 초기화는 일반사용자만 가능합니다.');
      return;
    }
    const newPassword = String(passwordDraft || '').trim();
    if (!newPassword) {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    try {
      setPasswordSaving(true);
      await adminResetUserPasswordApi(user.userId, newPassword);
      setPasswordOpenUserId(null);
      setPasswordDraft('');
      setPasswordResetDoneOpen(true);
    } catch (e) {
      if (e?.status === 401 || e?.suppressAlert) return;
      alert(e?.message || '비밀번호 초기화에 실패했습니다.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const getSelectedUsers = () => {
    if (!Array.isArray(users) || users.length === 0) return [];
    const setIds = new Set(selectedUserIds);
    return users.filter((u) => setIds.has(String(u.userId)));
  };

  const getEligibleSelectedUsers = (action) => {
    const selected = getSelectedUsers();
    if (action === 'register') return selected.filter((u) => u.status === 'ACTIVE');
    if (action === 'release') return selected.filter((u) => u.status === 'BLACKLIST');
    return selected;
  };

  const handleOpenBlacklistRegisterConfirm = () => {
    const eligible = getEligibleSelectedUsers('register');
    if (eligible.length === 0) {
      alert('선택된 회원 중 블랙리스트로 등록할 수 있는 일반사용자가 없습니다.');
      return;
    }
    setConfirmBlacklistRegisterOpen(true);
  };

  const handleOpenBlacklistReleaseConfirm = () => {
    const eligible = getEligibleSelectedUsers('release');
    if (eligible.length === 0) {
      alert('선택된 회원 중 블랙리스트 해제할 수 있는 사용자가 없습니다.');
      return;
    }
    setConfirmBlacklistReleaseOpen(true);
  };

  const handleCloseBlacklistRegisterConfirm = () => {
    setConfirmBlacklistRegisterOpen(false);
  };

  const handleCloseBlacklistReleaseConfirm = () => {
    setConfirmBlacklistReleaseOpen(false);
  };

  const handleConfirmBlacklistRegister = () => {
    (async () => {
      const eligible = getEligibleSelectedUsers('register');
      const ids = eligible.map((u) => u.userId);
      try {
        await adminBlacklistRegisterApi(ids);

        if (roleFilter === 'ACTIVE') {
          const setIds = new Set(ids.map((v) => String(v)));
          setUsers((prev) => prev.filter((u) => !setIds.has(String(u.userId))));
        } else {
          const setIds = new Set(ids.map((v) => String(v)));
          setUsers((prev) =>
            prev.map((u) => (setIds.has(String(u.userId)) ? { ...u, status: 'BLACKLIST' } : u))
          );
        }

        setSelectedUserIds([]);
        setConfirmBlacklistRegisterOpen(false);
      } catch (e) {
        if (e?.status === 401 || e?.suppressAlert) return;
        alert(e?.message || '블랙리스트 등록에 실패했습니다.');
      }
    })();
  };

  const handleConfirmBlacklistRelease = () => {
    (async () => {
      const eligible = getEligibleSelectedUsers('release');
      const ids = eligible.map((u) => u.userId);
      try {
        await adminBlacklistReleaseApi(ids);

        if (roleFilter === 'BLACKLIST') {
          const setIds = new Set(ids.map((v) => String(v)));
          setUsers((prev) => prev.filter((u) => !setIds.has(String(u.userId))));
        } else {
          const setIds = new Set(ids.map((v) => String(v)));
          setUsers((prev) =>
            prev.map((u) => (setIds.has(String(u.userId)) ? { ...u, status: 'ACTIVE' } : u))
          );
        }

        setSelectedUserIds([]);
        setConfirmBlacklistReleaseOpen(false);
      } catch (e) {
        if (e?.status === 401 || e?.suppressAlert) return;
        alert(e?.message || '블랙리스트 해제에 실패했습니다.');
      }
    })();
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          pb: 2,
          position: 'relative',
        }}
      >
        <ConfirmModal
          open={confirmChatOpen}
          message={
            selectedChatUser?.name
              ? `${selectedChatUser.name} 님과의 채팅방으로 이동할까요?`
              : '채팅방으로 이동할까요?'
          }
          confirmText="확인"
          cancelText="취소"
          onConfirm={confirmOpenChat}
          onCancel={closeChatConfirm}
        />
        <ConfirmModal
          open={confirmBlacklistRegisterOpen}
          message={`선택한 ${getEligibleSelectedUsers('register').length}명을 블랙리스트로 등록할까요?`}
          confirmText="등록"
          cancelText="취소"
          onConfirm={handleConfirmBlacklistRegister}
          onCancel={handleCloseBlacklistRegisterConfirm}
        />
        <ConfirmModal
          open={confirmBlacklistReleaseOpen}
          message={`선택한 ${getEligibleSelectedUsers('release').length}명의 블랙리스트를 해제할까요?`}
          confirmText="해제"
          cancelText="취소"
          onConfirm={handleConfirmBlacklistRelease}
          onCancel={handleCloseBlacklistReleaseConfirm}
        />
        <ConfirmModal
          open={passwordResetDoneOpen}
          message="비밀번호가 초기화되었습니다."
          confirmText="확인"
          onConfirm={() => setPasswordResetDoneOpen(false)}
        />
      <AdminManageModal open={adminManageOpen} onClose={() => setAdminManageOpen(false)} />
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigate}
        />

        <Header
          variant="full"
          onClickMenu={() => setMenuOpen(true)}
        />

        <AdminTabs />

        <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 1.2,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setAdminManageOpen(true)}
                  sx={{ fontWeight: 700, color: '#000', textTransform: 'none', minWidth: 0, p: 0 }}
                >
                <SupervisorAccountIcon sx={{ fontSize: 20 }} />
                  관리자 관리
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(roleFilter === 'all' || roleFilter === 'ACTIVE') && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenBlacklistRegisterConfirm}
                    disabled={selectedUserIds.length === 0}
                    sx={{
                      borderColor: '#ddd',
                      color: '#333',
                      fontWeight: 700,
                      '&.Mui-disabled': { borderColor: '#eee', color: '#bbb' },
                    }}
                  >
                    블랙리스트 등록
                  </Button>
                )}
                {(roleFilter === 'all' || roleFilter === 'BLACKLIST') && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenBlacklistReleaseConfirm}
                    disabled={selectedUserIds.length === 0}
                    sx={{
                      borderColor: '#ddd',
                      color: '#333',
                      fontWeight: 700,
                      '&.Mui-disabled': { borderColor: '#eee', color: '#bbb' },
                    }}
                  >
                    블랙리스트 해제
                  </Button>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: 1.5,
                  border: '1px solid #e0e0e0',
                  px: 0.75,
                  height: 40,
                  cursor: 'pointer',
                  width: 110,
                  flexShrink: 0,
                }}
                onClick={handleFilterClick}
              >
                <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500, pl: 0.75 }}>
                  {currentFilterLabel}
                </Typography>
                <KeyboardArrowDownIcon sx={{ fontSize: 18, ml: 0.5 }} />
              </Box>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="검색어를 입력해주세요"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: 20, color: '#9ca3af', mr: 0.5 }} />,
                  sx: {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: 1.75,
                  pr: 1.25,
                  height: 38,
                  borderRadius: 19,
                  border: '1px solid',
                  borderColor: isSortOpen ? '#103c85' : '#e5e7eb',
                  bgcolor: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    borderColor: '#103c85',
                    bgcolor: '#fafafa',
                    boxShadow: '0 4px 12px rgba(16, 60, 133, 0.08)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
                onClick={handleSortClick}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isSortOpen ? '#103c85' : '#4b5563',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {currentSortLabel}
                </Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    ml: 0.25,
                    color: isSortOpen ? '#103c85' : '#9ca3af',
                    transform: isSortOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Box>
            </Box>

            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
              disableScrollLock
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  mt: 0,
                  width: 110,
                },
              }}
            >
              {filterOptions.map((opt) => (
                <MenuItem
                  key={opt.key}
                  selected={roleFilter === opt.key}
                  onClick={() => handleFilterSelect(opt.key)}
                  sx={{ fontSize: 14, fontWeight: 500 }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>

            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
              disableScrollLock
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 100,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  '& .MuiList-root': {
                    p: 0.5,
                  },
                },
              }}
            >
              {sortOptions.map((opt) => (
                <MenuItem
                  key={opt.key}
                  selected={sort === opt.key}
                  onClick={() => handleSortSelect(opt.key)}
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 1.5,
                    py: 1,
                    px: 1.5,
                    my: 0.25,
                    color: '#4b5563',
                    '&.Mui-selected': {
                      bgcolor: '#f0f5ff',
                      color: '#103c85',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#e6eeff',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#f9fafb',
                    },
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>

            <Box>
              {usersError && (
                <Typography variant="body2" color="error" sx={{ py: 2 }}>
                  {usersError}
                </Typography>
              )}

              {!usersError && users.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  검색 조건에 맞는 회원이 없습니다.
                </Typography>
              )}

              {!usersError &&
                users.map((user) => {
                  const isPasswordOpen = String(passwordOpenUserId) === String(user.userId);
                  return (
                    <React.Fragment key={user.userId}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          pb: 1,
                        }}
                      >
                        <Checkbox
                          checked={selectedUserIds.includes(String(user.userId))}
                          onChange={() => toggleSelectedUser(user.userId)}
                          sx={{ p: 0, mr: 1.5 }}
                          color="default"
                        />

                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 14 }}>
                              {user.name}({user.loginId})  {renderOnlineBadge(user)}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center' }}
                          >
                            <Box component="span">{getStatusLabel(user.status)}</Box>
                            {user.ipAddress ? (
                              <>
                                <Box component="span" sx={{ color: '#9ca3af' }}>/</Box>
                                <Box component="span">IP: {user.ipAddress}</Box>
                              </>
                            ) : null}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.2 }}
                          >
                            <Box component="span">작성글 {user.newsCount ?? 0}</Box>
                            <Box component="span" sx={{ color: '#9ca3af' }}>/</Box>
                            <Box component="span">댓글 {user.commentCount ?? 0}</Box>
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => openChatConfirm(user)}
                              sx={{
                                color: '#666',
                                borderColor: '#ddd',
                                minWidth: '32px',
                                p: 0,
                                height: '24px',
                              }}
                            >
                              <ChatOutlinedIcon sx={{ fontSize: '1rem' }} />
                            </Button>
                            {canResetPassword(user) && (
                              <Button
                                variant={isPasswordOpen ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => togglePasswordPanel(user.userId)}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1,
                                  py: 0.2,
                                  minWidth: 'auto',
                                  borderColor: '#ddd',
                                  color: isPasswordOpen ? 'white' : '#666',
                                  bgcolor: isPasswordOpen ? '#103c85' : 'transparent',
                                  '&:hover': {
                                    bgcolor: isPasswordOpen ? '#0d3170' : '#f5f5f5',
                                  },
                                }}
                              >
                                비밀번호 초기화
                              </Button>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            가입일:{user.createdAt ? String(user.createdAt).slice(2, 10).replaceAll('-', '.') : '-'}
                          </Typography>
                        </Box>
                      </Box>

                      {isPasswordOpen && canResetPassword(user) && (
                        <Box sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              ml: 5.5,
                              mr: 0.5,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                px: 1,
                                py: 0.75,
                              }}
                            >
                              <input
                                type="password"
                                placeholder="비밀번호 입력"
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  outline: 'none',
                                  fontSize: 13,
                                }}
                                value={passwordDraft}
                                onChange={(e) => setPasswordDraft(e.target.value)}
                              />
                            </Box>
                            <Button
                              fullWidth
                              variant="contained"
                              disabled={passwordSaving}
                              onClick={() => handleResetPassword(user)}
                              sx={{
                                bgcolor: '#103c85',
                                fontWeight: 700,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#0d3170', boxShadow: 'none' },
                              }}
                            >
                              비밀번호 초기화
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
            </Box>
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_event, value) => setPage(value)}
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
        </Box>

      </Box>
    </Box>
  );
};

export default UserManagement;
